import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import Chat from './components/Chat/Chat';
import Dashboard from './components/Admin/Dashboard/Dashboard';
import Settings from './components/Admin/Settings/Settings';
import Changelog from './components/Admin/Changelog/Changelog';
import SocketProvider from './SocketProvider';
import DSGVO from './components/DSGVO/DSGVO';
import Prompts from './components/Admin/Prompts/Prompts';
import Prompt from './components/Admin/Prompt/Prompt';
import Groups from './components/Admin/Groups/Groups';
import Group from './components/Admin/Group/Group';
import PromptFunctions from './components/Admin/PromptFunctions/PromptFunctions';
import PromptFunction from './components/Admin/PromptFunction/PromptFunction';
import Layout from './components/Layout/Layout';
import { RouteWrapper } from './components/RouteWrapper/RouteWrapper';
import Models from './components/Admin/Models/Models';
import Model from './components/Admin/Model/Model';
import Styles from './components/Admin/Styles/Styles';
import Toaster from './components/Utility/Toaster';
import UserSettings from './components/UserSettings/UserSettings';
import { useDispatch, useSelector } from 'react-redux';
import Users from './components/Admin/Users/Users';
import User from './components/Admin/User/User';
import DemoModal from './components/Demo/DemoModal';
import { JoyrideWrapper } from './components/JoyrideWrapper/JoyrideWrapper';
import { setIsCtrlOrCmdPressed, setWindowWidth } from './Store/Actions/environmentActions';
import Modal from './components/Modal/Modal';
import Billing from './components/Admin/Billing/Billing';
import AddCredit from './components/Admin/AddCredit/AddCredit';
import { Tooltip } from 'react-tooltip';
import ApiKeys from './components/Admin/ApiKeys/ApiKeys';
import ApiKey from './components/Admin/ApiKey/ApiKey';
import Assistants from './components/Assistants/Assistants';
import Knowledgebases from './components/Admin/Knowledgebases/Knowledgebases';
import Knowledgebase from './components/Admin/Knowledgebase/Knowledgebase';
import EmbeddingModel from './components/Admin/EmbeddingModel/EmbeddingModel';
import UpdateView from './components/UpdateView/UpdateView';
import Credentials from './components/Admin/Credentials/Credentials';
import Credential from './components/Admin/Credential/Credential';
import FileDlpModal from './components/FileDlpModal/FileDlpModal';
import ComplianceDashboard from './components/Admin/ComplianceDashboard/ComplianceDashboard';
import OpenApiConfigPage from './components/Admin/PromptFunctions/components/OpenApiTab/OpenApiConfig';
import McpConfigPage from './components/Admin/PromptFunctions/components/McpTab/McpConfig';
import LogFiles from './components/Compliance/Landing/components/LogFiles';
import { setModal } from './Store/Actions/modalActions';
import { ModalData } from './Types';

function App() {
    const i18n = useSelector((state: any) => state.i18n.i18n);
    const demo = useSelector((state: any) => state.settings.demo);
    const systemStatus = useSelector<string>((state: any) => state.settings.systemStatus);
    const settings = useSelector((state: any) => state.settings);
    const userSettings = useSelector((state: any) => state.userSettings);
    const dispatch = useDispatch();

    const acceptedDSGVO =
        settings.dsgvoVersionTimestamp === 0 ||
        (userSettings.dsgvoAcceptedAt > 0 && userSettings.dsgvoAcceptedAt >= settings.dsgvoVersionTimestamp);

    useEffect(() => {
        let timeout: any;

        const handleResize = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                dispatch(setWindowWidth(window.innerWidth));
            }, 10);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
        };
    }, [dispatch]);

    //Maybe move to ShortcutHook later
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey) {
                dispatch(setIsCtrlOrCmdPressed(true));
            }
        };

        const handleKeyUp = () => {
            dispatch(setIsCtrlOrCmdPressed(false));
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [dispatch]);

    useEffect(() => {
        if (demo) {
            dispatch(
                setModal({
                    state: 'open',
                    title: i18n.translate({
                        id: 'modal.headline',
                        sourceName: 'Demo',
                    }),
                    modalSize: 'full',
                    showLeftButton: false,
                    showRightButton: false,
                    content: <DemoModal />,
                } as ModalData),
            );
        }
    }, [demo, dispatch, i18n]);

    return (
        <>
            <div className={`flex flex-col p-0 m-0 z-[1] relative`}>
                <Toaster />
                {demo && <DemoModal />}
                <Tooltip anchorSelect=".tooltip" delayShow={300} style={{ zIndex: 9999 }} />
                {acceptedDSGVO ? (
                    <Router>
                        <Modal />

                        <SocketProvider>
                            <FileDlpModal />
                            {systemStatus === 'updating' ? (
                                <UpdateView />
                            ) : (
                                <main className="main relative z-[1]">
                                    <Routes>
                                        <Route
                                            path="/"
                                            element={
                                                <JoyrideWrapper>
                                                    <Layout />
                                                </JoyrideWrapper>
                                            }
                                        >
                                            <Route path="" element={null}>
                                                <Route
                                                    path=""
                                                    element={
                                                        <RouteWrapper
                                                            title={i18n.translate({
                                                                id: 'title',
                                                                sourceName: 'ChatList',
                                                            })}
                                                            subTitle=""
                                                        >
                                                            <Chat />
                                                        </RouteWrapper>
                                                    }
                                                />
                                                <Route path="start" element={null}>
                                                    <Route
                                                        path=""
                                                        element={
                                                            <RouteWrapper
                                                                title={i18n.translate({
                                                                    id: 'title',
                                                                    sourceName: 'ChatList',
                                                                })}
                                                                subTitle=""
                                                            >
                                                                <Chat />
                                                            </RouteWrapper>
                                                        }
                                                    />
                                                    <Route
                                                        path=":assistantIdentifier"
                                                        element={
                                                            <RouteWrapper
                                                                title={i18n.translate({
                                                                    id: 'title',
                                                                    sourceName: 'ChatList',
                                                                })}
                                                                subTitle=""
                                                            >
                                                                <Chat />
                                                            </RouteWrapper>
                                                        }
                                                    >
                                                        <Route
                                                            path=":initMessage"
                                                            element={
                                                                <RouteWrapper
                                                                    title={i18n.translate({
                                                                        id: 'title',
                                                                        sourceName: 'ChatList',
                                                                    })}
                                                                    subTitle=""
                                                                >
                                                                    <Chat />
                                                                </RouteWrapper>
                                                            }
                                                        />
                                                    </Route>
                                                </Route>
                                            </Route>
                                            <Route path=":404Placeholder" element={<Navigate replace to="/" />} />
                                            <Route path="providerlogin" element={<Navigate replace to="/" />}>
                                                <Route path=":provider" element={<Navigate replace to="/" />} />
                                            </Route>
                                            <Route path="chats" element={null}>
                                                <Route
                                                    path=""
                                                    element={
                                                        <RouteWrapper title="" subTitle="">
                                                            <Chat />
                                                        </RouteWrapper>
                                                    }
                                                />
                                                <Route
                                                    path=":chatId"
                                                    element={
                                                        <RouteWrapper title="" subTitle="">
                                                            <Chat />
                                                        </RouteWrapper>
                                                    }
                                                />
                                            </Route>
                                            <Route
                                                path="userassistants"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title.ownAssistants',
                                                            sourceName: 'Prompts',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        {<Assistants />}
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="userassistants/:promptIdentifier"
                                                element={
                                                    <RouteWrapper title="Assistent" subTitle="">
                                                        <Prompt userPrompt={true} />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="usersettings"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'UserSettings',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <UserSettings />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/dashboard"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Dashboard' })}
                                                        subTitle=""
                                                    >
                                                        <Dashboard />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/settings"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Settings' })}
                                                        subTitle=""
                                                    >
                                                        <Settings />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/billing"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Billing' })}
                                                        subTitle=""
                                                    >
                                                        <Billing />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/credentials"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'Credentials',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <Credentials />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/credentials/:credentialIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'Credential',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <Credential />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/billing/addcredit"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'AddCredit' })}
                                                        subTitle=""
                                                    >
                                                        <AddCredit />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/models"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Models' })}
                                                        subTitle=""
                                                    >
                                                        <Models />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/models/:modelIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Model' })}
                                                        subTitle=""
                                                    >
                                                        <Model />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/openapiconfig/:configIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'OpenApiConfig',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <OpenApiConfigPage />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/embeddingmodels/:embeddingModelIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'EmbeddingModel',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <EmbeddingModel />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/assistants"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title.globalAssistants',
                                                            sourceName: 'Prompts',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <Prompts />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/assistants/:promptIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Prompt' })}
                                                        subTitle=""
                                                    >
                                                        <Prompt userPrompt={false} />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/assistantfunctions"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'PromptFunctions',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <PromptFunctions />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/assistantfunctions/:promptFunctionIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'PromptFunction',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <PromptFunction />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/groups"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Groups' })}
                                                        subTitle=""
                                                    >
                                                        <Groups />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/groups/:groupIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Group' })}
                                                        subTitle=""
                                                    >
                                                        <Group />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/apikeys"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'ApiKeys' })}
                                                        subTitle=""
                                                    >
                                                        <ApiKeys />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/apikeys/:apiKeyIdentifier"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'ApiKey' })}
                                                        subTitle=""
                                                    >
                                                        <ApiKey />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/users"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'users.title',
                                                            sourceName: 'Users',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <Users />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/users/:userIdentifier"
                                                element={
                                                    <RouteWrapper title="" subTitle="">
                                                        <User />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/changelog"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({ id: 'title', sourceName: 'Changelog' })}
                                                        subTitle=""
                                                    >
                                                        <Changelog />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/styles"
                                                element={
                                                    <RouteWrapper title="Styles" subTitle="">
                                                        <Styles />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/knowledgebases"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'Knowledgebases',
                                                        })}
                                                        subTitle={i18n.translate({
                                                            id: 'subTitle',
                                                            sourceName: 'Knowledgebases',
                                                        })}
                                                    >
                                                        <Knowledgebases />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/knowledgebases/:id"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'Knowledgebase',
                                                        })}
                                                        subTitle={i18n.translate({
                                                            id: 'subTitle',
                                                            sourceName: 'Knowledgebase',
                                                        })}
                                                    >
                                                        <Knowledgebase />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="compliance/logs"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'compliance.logs.title',
                                                            sourceName: 'LogFiles',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <LogFiles />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="compliance/dashboard"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'compliance.dashboard.title',
                                                            sourceName: 'LogFiles',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <ComplianceDashboard />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/openapi"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'title',
                                                            sourceName: 'OpenApiConfig',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <OpenApiConfigPage />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/mcpconfig/:configIdentifier"
                                                element={
                                                    <RouteWrapper title="MCP-Konfiguration" subTitle="">
                                                        <McpConfigPage />
                                                    </RouteWrapper>
                                                }
                                            />
                                            <Route
                                                path="admin/logs"
                                                element={
                                                    <RouteWrapper
                                                        title={i18n.translate({
                                                            id: 'logs.title',
                                                            sourceName: 'LogFiles',
                                                        })}
                                                        subTitle=""
                                                    >
                                                        <LogFiles />
                                                    </RouteWrapper>
                                                }
                                            />
                                        </Route>
                                    </Routes>
                                </main>
                            )}
                        </SocketProvider>
                    </Router>
                ) : (
                    <DSGVO />
                )}
            </div>
        </>
    );
}

export default App;
